/**
 * What a choice's picture needs to be seen on its tile.
 *
 * Library art — map pins, clipart, lettering, outlines — is usually a
 * transparent PNG or WebP. On the white choice tile a white pin is simply not
 * there, and black lettering on a black backdrop is not there either. So a
 * transparent picture gets a backdrop it contrasts with, chosen from its own
 * pixels (see `choice-display.ts` in @pz/scene for the whole rule set).
 *
 * An opaque picture (a style preview, a photo) covers its backdrop and fills
 * the tile; a transparent one is shown whole, with its empty margins cut off,
 * since a line of lettering in a square thumbnail otherwise reads at a third of
 * the tile's width.
 */

import type { ChoiceBackdrop, ChoiceFit } from '@pz/scene';

export type TileArt = 'opaque' | 'transparent' | 'light';

/** The visible part of a picture, as fractions of its width and height. */
export interface ArtBounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface TileArtReading {
  art: TileArt;
  /** Where the visible pixels are; undefined when the picture is opaque or empty. */
  bounds?: ArtBounds;
}

/** A pixel this transparent is "empty": it neither shows nor counts as art. */
const EMPTY_ALPHA = 16;

/**
 * Classifies RGBA pixels (as `getImageData` returns them).
 *
 * Transparent: a visible share of the picture lets the tile through.
 * Light: of what does show, most is near white — bright AND colourless; a
 * pastel pink card is bright but reads fine on the light backdrop. Every
 * pixel counts by its opacity, so the half-covered pixels of a thin white
 * outline still say "white" — thresholding them away left a white puzzle
 * outline on white.
 */
export function classifyTileArt(data: ArrayLike<number>): TileArt {
  let total = 0;
  let clear = 0;
  let weight = 0;
  let light = 0;
  for (let i = 0; i + 3 < data.length; i += 4) {
    total += 1;
    const alpha = data[i + 3]!;
    if (alpha < 200) clear += 1;
    if (alpha < EMPTY_ALPHA) continue;
    weight += alpha;
    const r = data[i]!;
    const g = data[i + 1]!;
    const b = data[i + 2]!;
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    const chroma = (Math.max(r, g, b) - Math.min(r, g, b)) / 255;
    if (luminance > 0.8 && chroma < 0.1) light += alpha;
  }
  if (total === 0 || clear / total < 0.05) return 'opaque';
  return weight > 0 && light / weight >= 0.6 ? 'light' : 'transparent';
}

/**
 * The box around every pixel that is not empty, as fractions; undefined when
 * nothing shows. `width` and `height` are the size of the sample the data came from.
 */
export function artBounds(data: ArrayLike<number>, width: number, height: number): ArtBounds | undefined {
  let left = width;
  let top = height;
  let right = -1;
  let bottom = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3]! < EMPTY_ALPHA) continue;
      if (x < left) left = x;
      if (x > right) right = x;
      if (y < top) top = y;
      if (y > bottom) bottom = y;
    }
  }
  if (right < 0) return undefined;
  return { x: left / width, y: top / height, w: (right - left + 1) / width, h: (bottom - top + 1) / height };
}

/**
 * The backdrop a question's tile gets: the question's own choice, or — on
 * `auto` — the dark one for light art and the light one for everything else.
 */
export function backdropFor(setting: ChoiceBackdrop, art: TileArt | undefined): 'light' | 'dark' {
  if (setting !== 'auto') return setting;
  return art === 'light' ? 'dark' : 'light';
}

/** Whether cutting the empty margins off is worth a new picture: at least 8% gone on a side pair. */
export function worthTrimming(bounds: ArtBounds | undefined): bounds is ArtBounds {
  return Boolean(bounds && (bounds.w <= 0.92 || bounds.h <= 0.92));
}

interface Probe {
  reading?: TileArtReading;
  image?: HTMLImageElement;
}

const known = new Map<string, Promise<Probe>>();
const trimmed = new Map<string, Promise<string | undefined>>();

/**
 * Reads a picture's pixels once per URL. No reading when they cannot be read —
 * a host without CORS, or no canvas at all — and the tile then keeps its plain
 * light backdrop, which is never worse than before.
 *
 * A separate CORS request on purpose: setting `crossOrigin` on the visible
 * `<img>` would make a picture from a host without CORS headers fail to show
 * at all, trading a faint pin for a broken one.
 */
function probe(url: string): Promise<Probe> {
  let answer = known.get(url);
  if (answer) return answer;
  answer = new Promise<Probe>((resolve) => {
    if (typeof Image === 'undefined' || typeof document === 'undefined') return resolve({});
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';
    image.onload = () => {
      try {
        // 64 px keeps a one-pixel outline visible after scaling, and is still
        // a trivial amount of work per picture.
        const side = 64;
        const canvas = document.createElement('canvas');
        canvas.width = side;
        canvas.height = side;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return resolve({});
        context.drawImage(image, 0, 0, side, side);
        const data = context.getImageData(0, 0, side, side).data;
        const art = classifyTileArt(data);
        resolve({ reading: { art, bounds: art === 'opaque' ? undefined : artBounds(data, side, side) }, image });
      } catch {
        resolve({}); // a tainted canvas: the host sent no CORS header
      }
    };
    image.onerror = () => resolve({});
    image.src = url;
  });
  known.set(url, answer);
  return answer;
}

/** What a picture's art is, once per URL; undefined when it cannot be read. */
export async function tileArtOf(url: string): Promise<TileArt | undefined> {
  return (await probe(url)).reading?.art;
}

/**
 * A copy of a transparent picture without its empty margins, as an object
 * URL; undefined when there is nothing worth cutting or it cannot be read.
 * Made once per URL, at the picture's own resolution (at most 512 px a side).
 */
function trimmedCopy(url: string): Promise<string | undefined> {
  let answer = trimmed.get(url);
  if (answer) return answer;
  answer = probe(url).then(({ reading, image }) => {
    const bounds = reading?.bounds;
    if (!image || !worthTrimming(bounds)) return undefined;
    const sx = bounds.x * image.naturalWidth;
    const sy = bounds.y * image.naturalHeight;
    const sw = bounds.w * image.naturalWidth;
    const sh = bounds.h * image.naturalHeight;
    const scale = Math.min(1, 512 / Math.max(sw, sh));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(sw * scale));
    canvas.height = Math.max(1, Math.round(sh * scale));
    const context = canvas.getContext('2d');
    if (!context || typeof canvas.toBlob !== 'function' || typeof URL.createObjectURL !== 'function') return undefined;
    context.drawImage(image, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    return new Promise<string | undefined>((resolve) => {
      try {
        canvas.toBlob((blob) => resolve(blob ? URL.createObjectURL(blob) : undefined), 'image/png');
      } catch {
        resolve(undefined);
      }
    });
  });
  trimmed.set(url, answer);
  return answer;
}

/**
 * Marks a picture's frame with what its art needs; see the tile styles.
 *
 * `has-alpha` shows the picture whole (contain) instead of filling the tile,
 * `is-dark-ground` puts it on the dark backdrop. With `fit` on `auto`, a
 * transparent picture's `<img>` is swapped for its trimmed copy.
 */
export function applyTileArt(
  frame: HTMLElement,
  image: HTMLImageElement,
  settings: { backdrop: ChoiceBackdrop; fit: ChoiceFit },
): void {
  // A question that fixes its backdrop needs no reading to show it.
  if (settings.backdrop !== 'auto') frame.classList.toggle('is-dark-ground', settings.backdrop === 'dark');
  const url = image.currentSrc || image.src;
  void probe(url).then(({ reading }) => {
    if (!reading) return;
    frame.classList.toggle('has-alpha', reading.art !== 'opaque');
    frame.classList.toggle('is-dark-ground', backdropFor(settings.backdrop, reading.art) === 'dark');
    if (settings.fit !== 'auto' || reading.art === 'opaque') return;
    void trimmedCopy(url).then((copy) => {
      // The tile may have been rebuilt, or its picture changed, meanwhile.
      if (!copy || (image.currentSrc || image.src) !== url) return;
      image.src = copy;
      frame.classList.add('is-trimmed');
    });
  });
}
